import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { LOCAL_STORAGE_KEYS } from '../../utils/constants';
import {
    Badge,
    Btn,
    Card,
    DataTable,
    IconImage,
    IconPencil,
    IconPdf,
    IconPlay,
    IconTrash,
    IconUpload,
    Pagination,
    SearchToolbar,
    Select,
} from '../ui';
import type { DataTableColumn, SearchSuggestOption, SortDirection } from '../ui';
import CardHeader from '../ui/headers/CardHeader';
import type { BriefingStyle, BriefingTopic } from './dailyBriefingData';
import { formatLastUsedLabel, isPdfBriefingTopic, isVideoBriefingTopic } from './dailyBriefingData';
import { StyleBadge } from './TodaysBriefingCard';

const PAGE_SIZE_OPTIONS = [25, 50, 100] as const;
type TopicPageSize = (typeof PAGE_SIZE_OPTIONS)[number];
type StyleFilter = 'All' | BriefingStyle;

const STYLE_FILTER_OPTIONS: { value: StyleFilter; label: string }[] = [
    { value: 'All', label: 'All' },
    { value: 'Video', label: 'Video' },
    { value: 'PDF', label: 'PDF' },
    { value: 'Poster', label: 'Poster' },
];

function previewActionForTopic(topic: BriefingTopic): {
    ariaLabel: string;
    icon: ReactNode;
} {
    if (isPdfBriefingTopic(topic)) {
        return { ariaLabel: `View ${topic.title} PDF`, icon: <IconPdf /> };
    }
    if (isVideoBriefingTopic(topic)) {
        return { ariaLabel: `Preview ${topic.title}`, icon: <IconPlay /> };
    }
    return { ariaLabel: `View ${topic.title} poster`, icon: <IconImage /> };
}

const PAGE_SIZE_SELECT_OPTIONS = PAGE_SIZE_OPTIONS.map((size) => ({
    value: String(size),
    label: `${size} per page`,
}));

function readStoredPageSize(): TopicPageSize {
    try {
        const raw = localStorage.getItem(LOCAL_STORAGE_KEYS.DAILY_BRIEFING_TOPIC_PAGE_SIZE);
        const parsed = Number(raw);
        if (PAGE_SIZE_OPTIONS.includes(parsed as TopicPageSize)) {
            return parsed as TopicPageSize;
        }
    } catch {
        // ignore storage errors
    }
    return 25;
}

function compareTopics(
    a: BriefingTopic,
    b: BriefingTopic,
    columnId: string | undefined,
    direction: SortDirection,
    columns: DataTableColumn<BriefingTopic>[],
): number {
    if (!columnId) return 0;
    const column = columns.find((c) => c.id === columnId);
    if (!column?.sortValue) return 0;

    const aVal = column.sortValue(a);
    const bVal = column.sortValue(b);
    let result = 0;

    if (column.sortType === 'numeric') {
        result = Number(aVal) - Number(bVal);
    } else {
        result = String(aVal).localeCompare(String(bVal), undefined, {
            numeric: true,
            sensitivity: 'base',
        });
    }

    return direction === 'desc' ? -result : result;
}

interface TopicLibraryCardProps {
    topics: BriefingTopic[];
    onImport: () => void;
    onPreview: (topic: BriefingTopic) => void;
    onEdit: (topic: BriefingTopic) => void;
    onDelete: (topic: BriefingTopic) => void;
}

export function TopicLibraryCard({ topics, onImport, onPreview, onEdit, onDelete }: TopicLibraryCardProps) {
    const [busyId, setBusyId] = useState<string | null>(null);
    const [pageSize, setPageSize] = useState<TopicPageSize>(() => readStoredPageSize());
    const [currentPage, setCurrentPage] = useState(1);
    const [sortColumnId, setSortColumnId] = useState<string | undefined>('topic');
    const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
    const [searchValue, setSearchValue] = useState('');
    const [styleFilter, setStyleFilter] = useState<StyleFilter>('All');

    const searchOptions = useMemo<SearchSuggestOption[]>(
        () =>
            topics.map((topic) => ({
                value: topic.id,
                label: `${topic.title} (${topic.displayId})`,
            })),
        [topics],
    );

    const filteredTopics = useMemo(() => {
        const query = searchValue.trim().toLowerCase();
        return topics.filter((topic) => {
            if (styleFilter !== 'All' && topic.style !== styleFilter) return false;
            if (!query) return true;
            if (topic.id.toLowerCase() === query) return true;
            return (
                topic.title.toLowerCase().includes(query) ||
                topic.displayId.toLowerCase().includes(query) ||
                `${topic.title} (${topic.displayId})`.toLowerCase().includes(query)
            );
        });
    }, [searchValue, styleFilter, topics]);

    const columns = useMemo<DataTableColumn<BriefingTopic>[]>(
        () => [
            {
                id: 'style',
                header: 'Style',
                renderCell: (row) => <StyleBadge style={row.style} />,
                sortable: true,
                sortValue: (row) => row.style,
            },
            {
                id: 'topic',
                header: 'Topic',
                mobileCardPrimary: true,
                renderCell: (row) => (
                    <div className="db-topic-cell">
                        <span className="db-topic-name">{row.title}</span>
                        <span className="db-topic-sub">{row.displayId} · Duration —</span>
                    </div>
                ),
                sortable: true,
                sortValue: (row) => row.title,
            },
            {
                id: 'duration',
                header: 'Duration',
                renderCell: () => '—',
            },
            {
                id: 'lastUsed',
                header: 'Last Used',
                renderCell: (row) => formatLastUsedLabel(row.lastUsedDate),
                sortable: true,
                sortValue: (row) => row.lastUsedDate ?? '',
            },
            {
                id: 'timesUsed',
                header: 'Times Used',
                renderCell: (row) => `${row.timesUsed}x`,
                sortable: true,
                sortType: 'numeric',
                sortValue: (row) => row.timesUsed,
            },
            {
                id: 'actions',
                header: 'Actions',
                mobileCardAction: true,
                renderCell: (row) => {
                    const preview = previewActionForTopic(row);
                    return (
                    <div className="db-row-actions">
                        <Btn
                            variant="icon"
                            ariaLabel={preview.ariaLabel}
                            buttonIcon={preview.icon}
                            buttonOnClick={() => onPreview(row)}
                            buttonDisabled={busyId === row.id}
                        />
                        <Btn
                            variant="icon"
                            ariaLabel={`Edit ${row.title}`}
                            buttonIcon={<IconPencil />}
                            buttonOnClick={() => onEdit(row)}
                            buttonDisabled={busyId === row.id}
                        />
                        <Btn
                            variant="icon"
                            ariaLabel={`Remove ${row.title} from library`}
                            buttonIcon={<IconTrash />}
                            buttonOnClick={async () => {
                                setBusyId(row.id);
                                try {
                                    await onDelete(row);
                                } finally {
                                    setBusyId(null);
                                }
                            }}
                            buttonDisabled={busyId === row.id}
                        />
                    </div>
                    );
                },
            },
        ],
        [busyId, onDelete, onEdit, onPreview],
    );

    const sortedTopics = useMemo(() => {
        const next = [...filteredTopics];
        next.sort((a, b) => compareTopics(a, b, sortColumnId, sortDirection, columns));
        return next;
    }, [columns, filteredTopics, sortColumnId, sortDirection]);

    const totalPages = Math.max(1, Math.ceil(sortedTopics.length / pageSize));

    useEffect(() => {
        if (currentPage > totalPages) {
            setCurrentPage(totalPages);
        }
    }, [currentPage, totalPages]);

    useEffect(() => {
        setCurrentPage(1);
    }, [searchValue, styleFilter]);

    const pageTopics = useMemo(() => {
        const pageIndex = Math.min(currentPage, totalPages);
        const start = (pageIndex - 1) * pageSize;
        return sortedTopics.slice(start, start + pageSize);
    }, [currentPage, pageSize, sortedTopics, totalPages]);

    const handlePageSizeChange = (value: string) => {
        const nextSize = Number(value) as TopicPageSize;
        if (!PAGE_SIZE_OPTIONS.includes(nextSize)) return;
        setPageSize(nextSize);
        setCurrentPage(1);
        try {
            localStorage.setItem(LOCAL_STORAGE_KEYS.DAILY_BRIEFING_TOPIC_PAGE_SIZE, String(nextSize));
        } catch {
            // ignore storage errors
        }
    };

    const rangeStart = sortedTopics.length === 0 ? 0 : (Math.min(currentPage, totalPages) - 1) * pageSize + 1;
    const rangeEnd = Math.min(Math.min(currentPage, totalPages) * pageSize, sortedTopics.length);

    const tableColumnCount = columns.length;
    const pageSizeFooterColSpan = 1;
    const paginationFooterColSpan = tableColumnCount - pageSizeFooterColSpan;

    const libraryTableFooter =
        filteredTopics.length > 0 ? (
            <tr className="db-library-pagination">
                <td colSpan={pageSizeFooterColSpan} className="db-library-page-size">
                    <span className="db-library-range" aria-live="polite">
                        Showing {rangeStart}–{rangeEnd} of {sortedTopics.length}
                    </span>
                    <Select
                        options={PAGE_SIZE_SELECT_OPTIONS}
                        value={String(pageSize)}
                        onChange={handlePageSizeChange}
                        ariaLabel="Topics per page"
                        className="db-page-size-select"
                    />
                </td>
                <td colSpan={paginationFooterColSpan} className="db-topic-pagination">
                    {totalPages > 1 ? (
                        <Pagination
                            currentPage={Math.min(currentPage, totalPages)}
                            totalPages={totalPages}
                            onPageChange={setCurrentPage}
                            ariaLabel="Topic library pagination"
                        />
                    ) : null}
                </td>
            </tr>
        ) : undefined;

    return (
        <Card variant="card-xl" cardClasses="card-pad-0 db-library-card">
            <CardHeader
                title={
                    <span className="db-library-title">
                        Topic Library
                        <Badge
                            badgeText={`${topics.length} topic${topics.length === 1 ? '' : 's'}`}
                            badgeClasses="db-topic-count-badge"
                        />
                    </span>
                }
                actions={
                    <Btn
                        variant="secondary-outlined"
                        buttonText="Import from Resources"
                        buttonIcon={<IconUpload />}
                        buttonOnClick={onImport}
                    />
                }
                paddingVariant="pad-lg"
            />
            <SearchToolbar
                searchInputId="db-library-search"
                searchAriaLabel="Search topic library"
                searchPlaceholder="Search library by name or ID..."
                searchOptions={searchOptions}
                searchValue={searchValue}
                onSearchChange={setSearchValue}
                allowNewSearch={false}
                filterOptions={STYLE_FILTER_OPTIONS}
                filterValue={styleFilter}
                onFilterChange={(value) => setStyleFilter(value as StyleFilter)}
                filterAriaLabel="Filter by style"
            />
            <div className="db-library-table">
                <DataTable
                    columns={columns}
                    rows={pageTopics}
                    getRowKey={(row) => row.id}
                    sortColumnId={sortColumnId}
                    sortDirection={sortDirection}
                    onSortChange={(columnId, direction) => {
                        setSortColumnId(columnId);
                        setSortDirection(direction);
                        setCurrentPage(1);
                    }}
                    emptyState={
                        <div className="db-empty-state">
                            {topics.length === 0
                                ? 'No briefing topics yet. Add a topic or import one from Resources.'
                                : 'No topics match your search or filter.'}
                        </div>
                    }
                    mobileCardLayout
                    tfoot={libraryTableFooter}
                    tfootClassName="db-library-tfoot"
                />
            </div>
        </Card>
    );
}
